import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const GOLD = '#D89232';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';
const ESPRESSO = '#2B2118';
const HAIR = '#E0D6CA';

const relDays = (d) => (d === 0 ? 'heute' : d === 1 ? 'seit gestern' : `seit ${d} Tagen`);

// Eine Zeile in "Zu erledigen": Playfair-Ziffer · Beschreibung · EIN Button
const TodoRow = ({ count, text, cta, onClick, testid }) => (
  <div className="flex items-center gap-6 py-5 border-b" style={{ borderColor: HAIR }} data-testid={testid}>
    <span className="font-display text-[40px] leading-none w-14 text-right flex-shrink-0" style={{ color: TERRA }}>
      {count}
    </span>
    <p className="flex-1 text-[15px] leading-snug" style={{ color: ESPRESSO }}>{text}</p>
    <button
      onClick={onClick}
      className="flex-shrink-0 h-[38px] px-6 rounded-full bg-transparent text-[11px] font-bold uppercase transition-colors"
      style={{ border: `1.5px solid ${TERRA}`, color: TERRA, letterSpacing: '0.16em' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = TERRA; e.currentTarget.style.color = '#F5F0EA'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TERRA; }}
      data-testid={`${testid}-cta`}
    >
      {cta}
    </button>
  </div>
);

const AdminDashboard = ({ Layout, getAuthHeader }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingSessions, setPendingSessions] = useState(0);
  const [pendingFeedback, setPendingFeedback] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [courseCount, setCourseCount] = useState(0);
  const [qualityCheck, setQualityCheck] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sess, feed, parts, courses] = await Promise.all([
        axios.get(`${API}/admin/pending-sessions`, getAuthHeader()).catch(() => ({ data: { pending_count: 0 } })),
        axios.get(`${API}/admin/tasks/pending-feedback`, getAuthHeader()).catch(() => ({ data: { pending_count: 0 } })),
        axios.get(`${API}/admin/all-participants`, getAuthHeader()).catch(() => ({ data: { participants: [] } })),
        axios.get(`${API}/courses`, getAuthHeader()).catch(() => ({ data: [] })),
      ]);
      setPendingSessions(sess.data.pending_count || 0);
      setPendingFeedback(feed.data.pending_count || 0);
      setParticipants(parts.data.participants || []);
      setCourseCount(Array.isArray(courses.data) ? courses.data.length : 0);
      try {
        const qc = await axios.get(`${API}/admin/quality-check/latest`, getAuthHeader());
        setQualityCheck(qc.data);
      } catch { /* silent */ }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // Fertig, aber noch keine Bescheinigung
  const readyForCert = participants.filter((p) =>
    (p.courses || []).some((c) => c.progress_percentage >= 100 && !c.certificate_issued)
  );

  // Braucht Aufmerksamkeit — max 5, automatisch befüllt
  const attention = [];
  participants.forEach((p) => {
    (p.courses || []).forEach((c) => {
      if (c.progress_percentage >= 100) return;
      if (c.days_stuck >= 14) {
        attention.push({
          type: 'inactive', user: p, course: c, weight: 1000 + c.days_stuck,
          reason: `Seit ${c.days_stuck} Tagen inaktiv in ${c.course_title}`,
        });
      } else if (c.progress_percentage >= 90) {
        attention.push({
          type: 'almost', user: p, course: c, weight: c.progress_percentage,
          reason: `Kurz vor Abschluss (${Math.round(c.progress_percentage)} %) in ${c.course_title}`,
        });
      }
    });
  });
  // pro Teilnehmer nur der wichtigste Eintrag
  const seen = new Set();
  const attentionTop = attention
    .sort((a, b) => b.weight - a.weight)
    .filter((a) => (seen.has(a.user.user_id) ? false : seen.add(a.user.user_id)))
    .slice(0, 5);

  const todos = [
    pendingSessions > 0 && {
      count: pendingSessions,
      text: pendingSessions === 1 ? 'Live-Session wartet auf Freigabe' : 'Live-Sessions warten auf Freigabe',
      cta: 'Freigeben',
      onClick: () => navigate('/admin/live-sessions'),
      testid: 'todo-sessions',
    },
    pendingFeedback > 0 && {
      count: pendingFeedback,
      text: pendingFeedback === 1 ? 'Eingereichte Aufgabe wartet auf dein Feedback' : 'Eingereichte Aufgaben warten auf dein Feedback',
      cta: 'Zur Korrektur',
      onClick: () => navigate('/admin/users?view=feedback'),
      testid: 'todo-feedback',
    },
    readyForCert.length > 0 && {
      count: readyForCert.length,
      text: readyForCert.length === 1 ? 'Teilnehmerin ist fertig — Bescheinigung ausstellen' : 'Teilnehmer sind fertig — Bescheinigungen ausstellen',
      cta: 'Ausstellen',
      onClick: () => navigate('/admin/users?view=ready_cert'),
      testid: 'todo-certificates',
    },
  ].filter(Boolean);

  const sendReminder = async (item) => {
    try {
      setSendingReminder(item.user.user_id);
      const res = await axios.post(
        `${API}/admin/inactivity-reminders/send?days=14&user_id=${encodeURIComponent(item.user.user_id)}`,
        {},
        getAuthHeader()
      );
      if (res.data.sent > 0) {
        toast.success(`Erinnerung an ${item.user.user_name} gesendet`);
      } else {
        toast.info('Keine Mail gesendet — Teilnehmer hat kürzlich schon eine Erinnerung erhalten.');
      }
    } catch (e) {
      toast.error('Erinnerung konnte nicht gesendet werden');
    } finally {
      setSendingReminder(null);
    }
  };

  const todayStr = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24" data-testid="admin-dashboard-loading">
          <Loader2 className="w-8 h-8 text-terra animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl space-y-12" data-testid="admin-dashboard">

        {/* QUALITÄTS-WÄCHTER — nur sichtbar bei Problemen */}
        {qualityCheck?.status === 'problems' && (
          <div className="px-5 py-4 flex items-start gap-3" style={{ border: '1.5px solid #E07A5F', backgroundColor: 'rgba(169,84,47,0.06)' }} data-testid="quality-warning">
            <span className="font-display text-[26px] leading-none flex-shrink-0" style={{ color: TERRA }}>!</span>
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.2em', color: TERRA }}>
                Qualitäts-Wächter · {new Date(qualityCheck.checked_at).toLocaleDateString('de-DE')}
              </p>
              <p className="text-[14px] mt-1" style={{ color: ESPRESSO }}>
                <strong>{qualityCheck.truncated_count}</strong> von {qualityCheck.total_checked} Kapiteln enden mitten im Satz
                {qualityCheck.truncated?.[0] && <> — z. B. „{qualityCheck.truncated[0].module_title || qualityCheck.truncated[0].module_id}"</>}.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/content-enhancer')}
              className="flex-shrink-0 h-[34px] px-5 rounded-full text-[10.5px] font-bold uppercase"
              style={{ border: `1.5px solid ${TERRA}`, color: TERRA, letterSpacing: '0.14em' }}
              data-testid="quality-warning-cta"
            >
              Beheben
            </button>
          </div>
        )}

        {/* C) AUF EINEN BLICK — Impressum-Zeile */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10.5px] uppercase font-semibold" style={{ letterSpacing: '0.18em', color: '#8A8175' }} data-testid="admin-glance-line">
          <button onClick={() => navigate('/admin/users')} className="hover:text-terra transition-colors" data-testid="glance-participants">
            {participants.length} Teilnehmer
          </button>
          <span style={{ color: GOLD_INK }}>·</span>
          <button onClick={() => navigate('/admin/courses')} className="hover:text-terra transition-colors" data-testid="glance-courses">
            {courseCount} Kurse
          </button>
          <span style={{ color: GOLD_INK }}>·</span>
          <button onClick={() => navigate('/admin/live-sessions')} className="hover:text-terra transition-colors" data-testid="glance-sessions">
            {pendingSessions} Sessions offen
          </button>
        </div>

        {/* Kopf */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-[2px] bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">
              Administration · {todayStr}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-espresso leading-[1.05]">
            Heu<span className="italic text-terra">te</span>.
          </h1>
        </div>

        {/* A) ZU ERLEDIGEN */}
        <div data-testid="admin-todo-section">
          <p className="font-mono text-[10px] uppercase font-semibold pb-3 border-b" style={{ letterSpacing: '0.24em', color: GOLD_INK, borderColor: HAIR }}>
            Zu erledigen
          </p>
          {todos.length === 0 ? (
            <p className="font-display italic text-[20px] text-sand-600 py-8" data-testid="admin-todo-empty">
              Alles erledigt — nichts wartet auf dich.
            </p>
          ) : (
            todos.map((t) => <TodoRow key={t.testid} {...t} />)
          )}
        </div>

        {/* B) BRAUCHT AUFMERKSAMKEIT */}
        <div data-testid="admin-attention-section">
          <div className="flex items-center justify-between pb-3 border-b" style={{ borderColor: HAIR }}>
            <p className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: GOLD_INK }}>
              Braucht Aufmerksamkeit
            </p>
            <button
              onClick={() => navigate('/admin/users?view=inactive')}
              className="text-[12.5px] font-semibold hover:underline underline-offset-4"
              style={{ color: TERRA }}
              data-testid="attention-see-all"
            >
              Alle ansehen →
            </button>
          </div>
          {attentionTop.length === 0 ? (
            <p className="font-display italic text-[17px] text-sand-600 py-7">
              Niemand fällt gerade auf — deine Teilnehmer sind gut unterwegs.
            </p>
          ) : (
            attentionTop.map((item) => (
              <div key={item.user.user_id} className="flex items-center gap-5 py-4 border-b" style={{ borderColor: HAIR }} data-testid={`attention-row-${item.user.username}`}>
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.type === 'inactive' ? TERRA : GOLD }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-[17px] leading-tight" style={{ color: ESPRESSO }}>{item.user.user_name}</p>
                  <p className="text-[13px] text-sand-600 mt-0.5 truncate">{item.reason}</p>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  {item.type === 'inactive' && (
                    <button
                      onClick={() => sendReminder(item)}
                      disabled={sendingReminder === item.user.user_id}
                      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold hover:underline underline-offset-4 disabled:opacity-50"
                      style={{ color: TERRA }}
                      data-testid={`attention-remind-${item.user.username}`}
                    >
                      {sendingReminder === item.user.user_id && <Loader2 className="w-3 h-3 animate-spin" />}
                      Erinnerung senden
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/admin/users?q=${encodeURIComponent(item.user.username)}`)}
                    className="inline-flex items-center gap-1 text-[12.5px] font-medium text-sand-600 hover:text-terra transition-colors"
                    data-testid={`attention-view-${item.user.username}`}
                  >
                    Ansehen
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Abschlusszeile */}
        <div className="pt-4 text-center">
          <div className="mx-auto mb-4" style={{ width: 60, height: 1, backgroundColor: GOLD }} />
          <p className="text-[10px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.28em' }}>
            Dickies-Campus Campus · Admin
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
